import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";
import { GroupedJourneys } from "../types/analytics/FilterAvailableValues";
import AnalyticsFilterInputs from "../types/analytics/AnalyticsFilterInputs";
import { applySavedFilterToPage } from "../helpers/analytics/applySavedFilterToPage";
import {
  buildSavedFilterDocument,
  selectionHasJourneyLayer,
} from "../helpers/analytics/buildSavedFilterDocument";
import { shouldWarnSavedFilterPointInTimeWithUserGroups } from "../helpers/analytics/savedFilterPointInTimeWarning";
import { labelMapFromCheckboxTree } from "../helpers/analytics/audienceFacetHelpers";
import {
  AnalyticsDateShortcut,
  AnalyticsSavedFilterDto,
  AnalyticsSavedFilterVisibility,
  buildPageKey,
  parseSavedFilterDocument,
} from "../types/analytics/AnalyticsSavedFilter";
import analyticsSavedFiltersApi, {
  isNameConflict,
} from "../api/analytics/analyticsSavedFiltersApi";
import FilterCheckboxTreeApiResponseDto from "../types/analytics/FilterCheckboxTreeApiResponseDto";
import locationApi from "../api/dashboard/locationApi";
import jobTitleApi from "../api/dashboard/jobTitleApi";
import appraisalLevelApi from "../api/dashboard/appraisalLevelApi";
import userGroupApi from "../api/admin/userGroupApi";
import UserContext from "../state/UserContext";
import permissionHelper from "../helpers/permissionHelper";

export interface UseAnalyticsSavedFiltersArgs {
  pageIdentifier: string;
  dynamicPageId?: number;
  selectedValues: AnalyticsFilterInputs;
  appliedValues: AnalyticsFilterInputs;
  availableJourneyKeys: string[];
  journeys: GroupedJourneys[];
  isDirty: boolean;
  appliedSavedFilterId: number | null;
  onAppliedSavedFilterIdChange(id: number | null): void;
  onRecallFilters(
    values: AnalyticsFilterInputs,
    savedFilterId?: number | null,
  ): void;
  onResetFilters(): void;
  /** Session carry-pin; used only for skipped page-default hint. */
  carryFiltersAcrossPages: boolean;
  applyPageDefaultOnLoad?: boolean;
  shareSavedFilterId?: number | null;
  onShareSavedFilterResolved?(result: "applied" | "failed"): void;
  onSavedFilterBootstrapComplete?(generation: number): void;
  savedFilterBootstrapGeneration?: number;
  /** Read-only for save document payload; owned by the filter band UI. */
  dateShortcut: AnalyticsDateShortcut;
  /** When a saved filter is recalled, sync committed date intent (chip + chrome). */
  onCommittedDateShortcutChange?(shortcut: AnalyticsDateShortcut): void;
}

/**
 * Saved-filter catalogue, bootstrap (?sf= / page default), CRUD dialogs,
 * and partial-apply banner state for the analytics filter band.
 */
export function useAnalyticsSavedFilters({
  pageIdentifier,
  dynamicPageId,
  selectedValues,
  appliedValues,
  availableJourneyKeys,
  journeys,
  isDirty,
  appliedSavedFilterId,
  onAppliedSavedFilterIdChange,
  onRecallFilters,
  onResetFilters,
  carryFiltersAcrossPages,
  applyPageDefaultOnLoad = false,
  shareSavedFilterId = null,
  onShareSavedFilterResolved,
  onSavedFilterBootstrapComplete,
  savedFilterBootstrapGeneration = 0,
  dateShortcut,
  onCommittedDateShortcutChange,
}: UseAnalyticsSavedFiltersArgs) {
  const { t } = useTranslation();
  const auth = useAuth();
  const userContext = useContext(UserContext);
  const savedApi = useMemo(
    () => new analyticsSavedFiltersApi(auth.user?.access_token),
    [auth.user?.access_token],
  );

  const pageKey = buildPageKey(dynamicPageId, pageIdentifier);
  const canManageShared = permissionHelper.userHasEitherPermission(
    userContext,
    "ManageSharedAnalyticsFilters",
    "MANAGE-SHARED-ANALYTICS-FILTERS",
  );
  const canPinPageDefault = canManageShared && dynamicPageId != null;

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDialogForceCreate, setSaveDialogForceCreate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveNameError, setSaveNameError] = useState<string | null>(null);
  const [savedItems, setSavedItems] = useState<AnalyticsSavedFilterDto[]>([]);
  const [renameItem, setRenameItem] = useState<AnalyticsSavedFilterDto | null>(
    null,
  );
  const [renameNameError, setRenameNameError] = useState<string | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [deleteConfirmItem, setDeleteConfirmItem] =
    useState<AnalyticsSavedFilterDto | null>(null);
  const [updateConfirm, setUpdateConfirm] = useState<{
    onSuccess?: () => void;
  } | null>(null);
  const [partialSkippedJourneys, setPartialSkippedJourneys] = useState<
    string[]
  >([]);
  const [partialBannerDismissed, setPartialBannerDismissed] = useState(false);

  const pageDefaultAppliedRef = useRef(false);
  const shareResolvedRef = useRef<number | null>(null);
  /** Latest bootstrap generation — async list/get must match before mutating. */
  const bootstrapGenerationRef = useRef(savedFilterBootstrapGeneration);
  bootstrapGenerationRef.current = savedFilterBootstrapGeneration;
  /**
   * Optional snapshot for save/update when the Advanced panel commits a draft
   * before React has flushed the outer selectedValues state.
   */
  const [documentOverride, setDocumentOverride] =
    useState<AnalyticsFilterInputs | null>(null);
  const locationLabelsRef = useRef<Map<number, string>>(new Map());
  const jobTitleLabelsRef = useRef<Map<number, string>>(new Map());
  const appraisalLevelLabelsRef = useRef<Map<number, string>>(new Map());
  const userGroupLabelsRef = useRef<Map<number, string>>(new Map());

  // Audience catalogues for portable id+label persistence on save/update.
  useEffect(() => {
    const token = auth.user?.access_token;
    new locationApi(token).getLocationCheckboxTreeItemsByClientId(
      false,
      (data: FilterCheckboxTreeApiResponseDto) => {
        locationLabelsRef.current = labelMapFromCheckboxTree(data.nodes);
      },
      (error) => console.error(error),
    );
    new jobTitleApi(token).getJobTitleCheckboxTreeItemsByClientId(
      (data: FilterCheckboxTreeApiResponseDto) => {
        jobTitleLabelsRef.current = labelMapFromCheckboxTree(data.nodes);
      },
      (error) => console.error(error),
    );
    new appraisalLevelApi(token).getAppraisalLevelCheckboxTreeItemsByClientId(
      (data: FilterCheckboxTreeApiResponseDto) => {
        appraisalLevelLabelsRef.current = labelMapFromCheckboxTree(data.nodes);
      },
      (error) => console.error(error),
    );
    new userGroupApi(token).getAnalyticsUserGroups(
      (data) => {
        userGroupLabelsRef.current = new Map(
          data.map((g) => [g.id, g.name] as [number, string]),
        );
      },
      (error) => console.error(error),
    );
  }, [auth.user?.access_token]);

  const appliedSavedFilterName = useMemo(() => {
    if (appliedSavedFilterId == null || isDirty) return null;
    return (
      savedItems.find((item) => item.id === appliedSavedFilterId)?.name ?? null
    );
  }, [appliedSavedFilterId, savedItems, isDirty]);

  const appliedSavedItem = useMemo(
    () =>
      appliedSavedFilterId == null
        ? null
        : (savedItems.find((item) => item.id === appliedSavedFilterId) ?? null),
    [appliedSavedFilterId, savedItems],
  );

  const pageDefaultItem = useMemo(
    () => savedItems.find((item) => item.isPageDefault) ?? null,
    [savedItems],
  );

  // Carry pin on + this page has a default that is not the applied save (incl. none).
  const isSkippingPageDefault =
    carryFiltersAcrossPages &&
    pageDefaultItem != null &&
    appliedSavedFilterId !== pageDefaultItem.id;

  const isPartialApply = partialSkippedJourneys.length > 0;
  const canUpdateApplied =
    isDirty &&
    appliedSavedItem != null &&
    appliedSavedItem.canEdit &&
    !isPartialApply;
  const valuesForSaveDocument = documentOverride ?? selectedValues;
  const isPageBoundSave = selectionHasJourneyLayer(valuesForSaveDocument);
  /** Show past data date that this save would store, or null when live. */
  const pointInTimeDateForSave =
    valuesForSaveDocument.pointInTimeDate?.trim() || null;
  const showUserGroupsPastDataWarning =
    shouldWarnSavedFilterPointInTimeWithUserGroups({
      pointInTimeDate: valuesForSaveDocument.pointInTimeDate,
      userGroupIds: valuesForSaveDocument.userGroupIds,
    });

  const clearPartialApplyState = () => {
    setPartialSkippedJourneys([]);
    setPartialBannerDismissed(false);
  };

  const setPartialFromSkipped = (skipped: string[]) => {
    setPartialSkippedJourneys(skipped);
    setPartialBannerDismissed(false);
  };

  const handleRecall = (item: AnalyticsSavedFilterDto) => {
    const doc = parseSavedFilterDocument(item.filterJson);
    if (!doc) return;
    const result = applySavedFilterToPage({
      doc,
      availableJourneyKeys,
      pageIdentifier,
      pageFallback: {
        allJourneys: selectedValues.allJourneys,
        journeyKeys: selectedValues.journeyKeys,
        dateRange: selectedValues.dateRange,
      },
    });
    onCommittedDateShortcutChange?.(doc.journey?.dateShortcut ?? "CUSTOM");
    onRecallFilters(result.inputs, item.id);
    onAppliedSavedFilterIdChange(item.id);
    setPartialFromSkipped(result.skippedJourneyLabels);
  };

  const loadSaved = () => {
    const generationAtStart = savedFilterBootstrapGeneration;
    const isStale = () =>
      generationAtStart !== bootstrapGenerationRef.current;
    const finishBootstrap = () => {
      if (isStale()) return;
      onSavedFilterBootstrapComplete?.(generationAtStart);
    };

    savedApi.list(
      pageKey,
      dynamicPageId,
      (data) => {
        if (isStale()) return;
        setSavedItems(data);

        const applyPageDefaultIfNeeded = () => {
          if (isStale()) return;
          if (
            applyPageDefaultOnLoad &&
            !pageDefaultAppliedRef.current &&
            appliedSavedFilterId == null
          ) {
            const pageDefault = data.find((x) => x.isPageDefault);
            if (pageDefault) {
              pageDefaultAppliedRef.current = true;
              handleRecall(pageDefault);
            }
          }
        };

        if (
          shareSavedFilterId != null &&
          shareResolvedRef.current !== shareSavedFilterId
        ) {
          // Already applied this share id (e.g. after a prior resolve) - do not
          // recall again or filterAppliedValues churn retriggers widget reload.
          if (appliedSavedFilterId === shareSavedFilterId) {
            shareResolvedRef.current = shareSavedFilterId;
            finishBootstrap();
            return;
          }
          shareResolvedRef.current = shareSavedFilterId;
          savedApi.get(
            shareSavedFilterId,
            dynamicPageId,
            (item) => {
              if (isStale()) return;
              const doc = parseSavedFilterDocument(item.filterJson);
              if (!doc) {
                shareResolvedRef.current = null;
                onShareSavedFilterResolved?.("failed");
                applyPageDefaultIfNeeded();
                finishBootstrap();
                return;
              }
              handleRecall(item);
              pageDefaultAppliedRef.current = true;
              onShareSavedFilterResolved?.("applied");
              finishBootstrap();
            },
            () => {
              if (isStale()) return;
              shareResolvedRef.current = null;
              onShareSavedFilterResolved?.("failed");
              applyPageDefaultIfNeeded();
              finishBootstrap();
            },
          );
          return;
        }

        if (shareSavedFilterId == null) {
          applyPageDefaultIfNeeded();
        }
        finishBootstrap();
      },
      (err) => {
        if (isStale()) return;
        console.error("load saved filters", err);
        finishBootstrap();
      },
    );
  };

  useEffect(() => {
    pageDefaultAppliedRef.current = false;
    loadSaved();
  }, [pageKey, dynamicPageId, shareSavedFilterId, savedFilterBootstrapGeneration]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recompute partial-apply skips when the page's journey catalogue changes while a save stays attached.
  useEffect(() => {
    if (appliedSavedFilterId == null || appliedSavedItem == null) {
      if (appliedSavedFilterId == null) {
        clearPartialApplyState();
      }
      return;
    }
    const doc = parseSavedFilterDocument(appliedSavedItem.filterJson);
    if (!doc) {
      clearPartialApplyState();
      return;
    }
    const result = applySavedFilterToPage({
      doc,
      availableJourneyKeys,
      pageIdentifier,
      pageFallback: {
        allJourneys: appliedValues.allJourneys,
        journeyKeys: appliedValues.journeyKeys,
        dateRange: appliedValues.dateRange,
      },
    });
    setPartialSkippedJourneys((prev) => {
      const next = result.skippedJourneyLabels;
      if (
        prev.length === next.length &&
        prev.every((label, i) => label === next[i])
      ) {
        return prev;
      }
      return next;
    });
    if (result.skippedJourneyLabels.length === 0) {
      setPartialBannerDismissed(false);
    }
  }, [
    appliedSavedFilterId,
    appliedSavedItem,
    availableJourneyKeys,
    pageIdentifier,
    appliedValues.allJourneys,
    appliedValues.journeyKeys,
    appliedValues.dateRange,
  ]);

  const openSaveDialog = (
    forceCreateOnly = false,
    snapshot?: AnalyticsFilterInputs,
  ) => {
    setDocumentOverride(snapshot ?? null);
    setSaveNameError(null);
    setSaveDialogForceCreate(forceCreateOnly);
    setSaveDialogOpen(true);
  };

  const setSaveDialogOpenAndClearOverride = (open: boolean) => {
    if (!open) {
      setDocumentOverride(null);
    }
    setSaveDialogOpen(open);
  };

  const buildCurrentDocumentJson = (override?: AnalyticsFilterInputs) => {
    const selected = override ?? documentOverride ?? selectedValues;
    const doc = buildSavedFilterDocument({
      selected,
      journeys,
      includeJourneyLayer: selectionHasJourneyLayer(selected),
      pageKey,
      dateShortcut,
      locationLabels: locationLabelsRef.current,
      jobTitleLabels: jobTitleLabelsRef.current,
      appraisalLevelLabels: appraisalLevelLabelsRef.current,
      userGroupLabels: userGroupLabelsRef.current,
    });
    return JSON.stringify(doc);
  };

  const handleSave = (args: {
    name: string;
    visibility: AnalyticsSavedFilterVisibility;
  }) => {
    setIsSaving(true);
    setSaveNameError(null);
    const snapshot = documentOverride ?? selectedValues;
    savedApi.create(
      {
        name: args.name,
        visibility: args.visibility,
        filterJson: buildCurrentDocumentJson(snapshot),
        pageKey,
      },
      (created) => {
        setIsSaving(false);
        setSaveDialogOpen(false);
        setDocumentOverride(null);
        onRecallFilters(snapshot, created.id);
        onAppliedSavedFilterIdChange(created.id);
        setSavedItems((prev) =>
          prev.some((item) => item.id === created.id)
            ? prev
            : [created, ...prev],
        );
        loadSaved();
      },
      (err) => {
        setIsSaving(false);
        console.error(err);
        if (isNameConflict(err)) {
          setSaveNameError(
            t(
              "Analytics.Filters.SaveDialog.NameConflict",
              "A saved filter with this name already exists.",
            ),
          );
          return;
        }
        alert(err?.message || "Could not save filter");
      },
    );
  };

  const handleUpdate = (
    onSuccess?: () => void,
    snapshot?: AnalyticsFilterInputs,
  ) => {
    if (appliedSavedItem == null || !appliedSavedItem.canEdit) return;
    if (snapshot) {
      setDocumentOverride(snapshot);
    }
    // Confirm sits above the save dialog — close save first so only one modal shows.
    setSaveDialogOpen(false);
    setUpdateConfirm({ onSuccess });
  };

  const confirmUpdate = () => {
    if (appliedSavedItem == null || !appliedSavedItem.canEdit) {
      setUpdateConfirm(null);
      return;
    }
    const onSuccess = updateConfirm?.onSuccess;
    setUpdateConfirm(null);
    const snapshot = documentOverride ?? selectedValues;

    setIsSaving(true);
    savedApi.update(
      appliedSavedItem.id,
      {
        name: appliedSavedItem.name,
        visibility: appliedSavedItem.visibility,
        filterJson: buildCurrentDocumentJson(snapshot),
        pageKey,
      },
      (updated) => {
        setIsSaving(false);
        setSaveDialogOpen(false);
        setDocumentOverride(null);
        // Keep the named save attached after Update (unlike Apply, which detaches).
        onRecallFilters(snapshot, updated.id);
        onAppliedSavedFilterIdChange(updated.id);
        setSavedItems((prev) =>
          prev.map((item) => (item.id === updated.id ? updated : item)),
        );
        loadSaved();
        onSuccess?.();
      },
      (err) => {
        setIsSaving(false);
        console.error(err);
        alert(err?.message || "Could not update filter");
      },
    );
  };

  const handleDelete = (item: AnalyticsSavedFilterDto) => {
    setDeleteConfirmItem(item);
  };

  const confirmDelete = () => {
    const item = deleteConfirmItem;
    if (item == null) return;
    setDeleteConfirmItem(null);
    savedApi.delete(
      item.id,
      () => {
        if (appliedSavedFilterId === item.id) {
          onAppliedSavedFilterIdChange(null);
        }
        loadSaved();
      },
      (err) => console.error(err),
    );
  };

  const updateConfirmBody = (() => {
    if (appliedSavedItem == null) return "";
    let message = t(
      "Analytics.Filters.SaveDialog.UpdateConfirm",
      "Update '{{name}}' with your current filters?",
      { name: appliedSavedItem.name },
    );
    if (appliedSavedItem.visibility === "SHARED") {
      message +=
        " " +
        t(
          "Analytics.Filters.SaveDialog.UpdateConfirmShared",
          "This changes it for everyone.",
        );
    }
    if (appliedSavedItem.isPageDefault) {
      message +=
        " " +
        t(
          "Analytics.Filters.SaveDialog.UpdateConfirmPageDefault",
          "This is the default for this page.",
        );
    }
    if (showUserGroupsPastDataWarning) {
      message +=
        " " +
        t(
          "Analytics.Filters.SaveDialog.PastDataUserGroupsWarning",
          "User groups stay current even with a data snapshot. Prefer a group named for this period, and create a new group later, instead of editing the same living group.",
        );
    }
    return message;
  })();

  const deleteConfirmBody =
    deleteConfirmItem == null
      ? ""
      : deleteConfirmItem.isPageDefault
        ? t(
            "Analytics.Filters.Saved.DeleteConfirmPageDefault",
            'Delete "{{name}}"? This will also clear it as the page default.',
            { name: deleteConfirmItem.name },
          )
        : t("Analytics.Filters.Saved.DeleteConfirm", 'Delete "{{name}}"?', {
            name: deleteConfirmItem.name,
          });

  const handleRenameSubmit = (name: string) => {
    if (renameItem == null) return;
    setIsRenaming(true);
    setRenameNameError(null);
    savedApi.rename(
      renameItem.id,
      name,
      dynamicPageId,
      (updated) => {
        setIsRenaming(false);
        setRenameItem(null);
        setSavedItems((prev) =>
          prev.map((item) => (item.id === updated.id ? updated : item)),
        );
        loadSaved();
      },
      (err) => {
        setIsRenaming(false);
        if (isNameConflict(err)) {
          setRenameNameError(
            t(
              "Analytics.Filters.SaveDialog.NameConflict",
              "A saved filter with this name already exists.",
            ),
          );
          return;
        }
        alert(err?.message || "Could not rename filter");
      },
    );
  };

  const handleShare = async (item: AnalyticsSavedFilterDto) => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("sf", String(item.id));
      await navigator.clipboard.writeText(url.toString());
      window.alert(
        t(
          "Analytics.Filters.Saved.ShareCopied",
          "Share link copied to the clipboard.",
        ),
      );
    } catch (err) {
      console.error(err);
      window.alert(
        t(
          "Analytics.Filters.Saved.ShareCopyFailed",
          "Could not copy the share link.",
        ),
      );
    }
  };

  const handleSetPageDefault = (item: AnalyticsSavedFilterDto) => {
    if (dynamicPageId == null) return;
    savedApi.setPageDefault(
      dynamicPageId,
      item.id,
      () => loadSaved(),
      (err) => console.error(err),
    );
  };

  const handleClearPageDefault = () => {
    if (dynamicPageId == null) return;
    savedApi.setPageDefault(
      dynamicPageId,
      null,
      () => loadSaved(),
      (err) => console.error(err),
    );
  };

  const clearSavedSelection = () => {
    onResetFilters();
    clearPartialApplyState();
  };

  const openRename = (item: AnalyticsSavedFilterDto) => {
    setRenameNameError(null);
    setRenameItem(item);
  };

  const closeRename = () => {
    setRenameItem(null);
    setRenameNameError(null);
  };

  return {
    pageKey,
    canManageShared,
    canPinPageDefault,
    savedItems,
    appliedSavedFilterName,
    appliedSavedItem,
    isSkippingPageDefault,
    isPartialApply,
    canUpdateApplied,
    isPageBoundSave,
    pointInTimeDateForSave,
    showUserGroupsPastDataWarning,
    partialSkippedJourneys,
    partialBannerDismissed,
    dismissPartialBanner: () => setPartialBannerDismissed(true),
    clearPartialApplyState,
    recall: handleRecall,
    share: handleShare,
    setPageDefault: handleSetPageDefault,
    clearPageDefault: handleClearPageDefault,
    clearSavedSelection,
    saveDialogOpen,
    setSaveDialogOpen: setSaveDialogOpenAndClearOverride,
    saveDialogForceCreate,
    isSaving,
    saveNameError,
    clearSaveNameError: () => setSaveNameError(null),
    openSaveDialog,
    handleSave,
    handleUpdate,
    confirmUpdate,
    updateConfirm,
    setUpdateConfirm,
    updateConfirmBody,
    renameItem,
    setRenameItem,
    renameNameError,
    isRenaming,
    openRename,
    closeRename,
    handleRenameSubmit,
    deleteConfirmItem,
    setDeleteConfirmItem,
    handleDelete,
    confirmDelete,
    deleteConfirmBody,
  };
}

export default useAnalyticsSavedFilters;
